import { SSE_HEADER_QUERY_PARAMS } from '../constants/headers.constants';
import { IProject } from '../interfaces/IProject';
import { UserService } from './user.service';
import { ProjectService } from './project.service';
import { captureMessage } from '../utils/monitoring.util';

const STREAM_RECONNECT_MAX_ATTEMPTS: number = 5;
const STREAM_RECONNECT_BASE_DELAY_MS: number = 3000;
const STREAM_RECONNECT_MAX_DELAY_MS: number = 30000;

class StreamService {
	private config: any;
	private userService: UserService;
	private projectService: ProjectService;
	private openStreams: any;
	private waitingStreams: any;
	private reconnectTimeouts: Record<string, ReturnType<typeof setTimeout>>;
	// Bumped on every close; `openStream` captures it before its async refresh gap and bails
	// if it changed, so a stream closed DURING that gap is never (re)opened into a zombie.
	private openGenerations: Record<string, number>;
	private onOpenStreamInterceptor: any;
	private onErrorStreamInterceptor: any;

	constructor(config: any, userService: UserService, projectService: ProjectService) {
		this.config = config;
		this.userService = userService;
		this.projectService = projectService;
		this.openStreams = {};
		this.waitingStreams = {};
		this.reconnectTimeouts = {};
		this.openGenerations = {};
	}

	setOnOpenStreamInterceptor(callback) {
		this.onOpenStreamInterceptor = callback;
	}

	setOnErrorStreamInterceptor(callback) {
		this.onErrorStreamInterceptor = callback;
	}

	async openStream(api, onData, onError?: (event: Event) => void, reconnectAttempts: number = 0) {
		if (this.waitingStreams[api]) {
			return;
		}
		this.waitingStreams[api] = true;
		// Snapshot the close-generation before the async refresh gap below.
		const generationAtStart: number = this.openGenerations[api] || 0;

		try {
			if (this.onOpenStreamInterceptor) {
				try {
					await this.onOpenStreamInterceptor();
				} catch (e: any) {
					// A transient token refresh failure must NOT leave the stream wedged (it
					// used to throw out of here with waitingStreams stuck true, so the stream
					// could never reopen). Proceed with the current token — the connection will
					// error and reconnect, bounded by the max-attempts cap, exactly as the
					// error-path interceptor already does.
					// Endpoint and error text in metadata, not in the grouping key — see the
					// note on `onerror` below.
					const [endpoint, queryString] = api.split('?');
					captureMessage('StreamService: open interceptor failed', {
						endpoint,
						queryString,
						errMessage: e?.message,
					});
				}
			}

			// If a close was requested during the await gap, abort instead of opening a
			// connection the caller already tore down (the lost-close / zombie-EventSource race).
			if ((this.openGenerations[api] || 0) !== generationAtStart) {
				return;
			}

			this.closeStreamIncludes(api.split('?')[0]);

			let currentReconnectAttempts: number = reconnectAttempts;
			const eventSource = new EventSource(`${this.config.apiGateway}${api}${this.getAuthQueryParams()}`);
			this.openStreams[api] = eventSource;
			eventSource.onmessage = onData;
			eventSource.onopen = () => {
				currentReconnectAttempts = 0;
			};
			eventSource.onerror = (event: Event) => {
				// The message is the Sentry grouping key, so it must not carry the endpoint or
				// the projectId: interpolating them split one broken-reconnect bug across 14
				// issues, one per stream per project, which nobody could triage. The detail
				// belongs in metadata, where it is filterable instead.
				//
				// `${event}` also stringified a DOM Event to the literal '[object Event]', so
				// ~1,900 reports said a stream broke and none said why. EventSource surfaces
				// nothing else useful on the event itself, so readyState is the real signal:
				// 0 CONNECTING (retrying), 1 OPEN, 2 CLOSED (dead, will not retry itself).
				const [endpoint, queryString] = api.split('?');
				captureMessage('StreamService: stream error', {
					endpoint,
					queryString,
					readyState: eventSource.readyState,
					eventType: event.type,
					reconnectAttempts: currentReconnectAttempts,
				});
				this.handleStreamError(api, onData, onError, currentReconnectAttempts, event);
			};
		} finally {
			this.waitingStreams[api] = false;
		}
	}

	closeStream(api) {
		// Bump first: a close requested while an openStream for this api is mid-await must be
		// visible to that suspended call when it resumes (see openStream's generation guard).
		this.openGenerations[api] = (this.openGenerations[api] || 0) + 1;
		this.clearPendingReconnect(api);
		if (this.openStreams[api]) {
			this.openStreams[api].close();
			delete this.openStreams[api];
		}
	}

	closeStreamIncludes(api) {
		for (const openStream in this.openStreams) {
			if (openStream.includes(api)) {
				this.closeStream(openStream);
			}
		}
		for (const reconnectingStream in this.reconnectTimeouts) {
			if (reconnectingStream.includes(api)) {
				this.clearPendingReconnect(reconnectingStream);
			}
		}
	}

	closeAllStreams() {
		for (const openStream in this.openStreams) {
			this.closeStream(openStream);
		}
		for (const reconnectingStream in this.reconnectTimeouts) {
			this.clearPendingReconnect(reconnectingStream);
		}
	}

	// EventSource reconnects on its own, but always with the token of the ORIGINAL query string.
	// With a short lived access token every such reconnect is dead on arrival, so the broken stream
	// is closed and re-opened with the current token instead (the error interceptor refreshes it).
	private handleStreamError = async (
		api,
		onData,
		onError: ((event: Event) => void) | undefined,
		previousReconnectAttempts: number,
		event: Event
	): Promise<void> => {
		this.closeStream(api);

		const reconnectAttempts: number = previousReconnectAttempts + 1;
		if (reconnectAttempts > STREAM_RECONNECT_MAX_ATTEMPTS) {
			// Same grouping rule as `onerror` above, which these two calls did not follow: the
			// message is the Sentry grouping key, so the endpoint, the projectId in its query
			// string and the underlying error text all belong in metadata. Interpolated, they
			// split one bug across an issue per stream per project per failure mode — which is
			// why the refresh failures of FRONTEND-3H6 surfaced as SEVERAL `StreamService`
			// issues rather than one.
			const [endpoint, queryString] = api.split('?');
			captureMessage('StreamService: giving up on stream', {
				endpoint,
				queryString,
				reconnectAttempts: previousReconnectAttempts,
			});
			if (onError) {
				onError(event);
			}
			return;
		}

		const reconnectTimeout = setTimeout(async () => {
			if (this.onErrorStreamInterceptor) {
				try {
					await this.onErrorStreamInterceptor(api, onData);
				} catch (e: any) {
					const [endpoint, queryString] = api.split('?');
					captureMessage('StreamService: failed to refresh token before reconnecting stream', {
						endpoint,
						queryString,
						errMessage: e?.message,
					});
				}
			}

			if (this.reconnectTimeouts[api] !== reconnectTimeout) {
				return;
			}
			delete this.reconnectTimeouts[api];
			await this.openStream(api, onData, onError, reconnectAttempts);
		}, this.getReconnectDelayMS(reconnectAttempts));
		this.reconnectTimeouts[api] = reconnectTimeout;
	};

	private clearPendingReconnect = (api): void => {
		if (this.reconnectTimeouts[api]) {
			clearTimeout(this.reconnectTimeouts[api]);
			delete this.reconnectTimeouts[api];
		}
	};

	private getReconnectDelayMS = (reconnectAttempts: number): number =>
		Math.min(STREAM_RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempts - 1), STREAM_RECONNECT_MAX_DELAY_MS);

	getAuthQueryParams = () => {
		const requestProject: IProject | undefined = this.projectService.getChosenProject();
		const accessToken = this.userService.getLoggedUserAccessToken();
		const loggedUserDetails = this.userService.getUserDetails();
		return `&${SSE_HEADER_QUERY_PARAMS.accessToken}=${accessToken}\
	&${SSE_HEADER_QUERY_PARAMS.tz}=${requestProject?.tz}\
	&${SSE_HEADER_QUERY_PARAMS.projectId}=${requestProject?.projectId}\
	&${SSE_HEADER_QUERY_PARAMS.organizationId}=${requestProject?.organizationId}\
	&${SSE_HEADER_QUERY_PARAMS.username}=${loggedUserDetails?.username}\
	&${SSE_HEADER_QUERY_PARAMS.isDemo}=${!!requestProject?.isDemo}`;
	};
}

export { StreamService };
